import React, { useState } from "react";
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  LinearProgress,
  Stack,
  Divider
} from "@mui/material";

const insightsData = [
  {
    id: 1,
    title: "Week 3: Recursive Problem Solving",
    date: "March 05, 2024",
    category: "Algorithm Analysis",
    priority: "High",
    description: "Students struggled with base case definition and recursive step formulation in dynamic programming. Common issues included infinite loops and incorrect state transitions for memoization. Recommended practice on memoization tables.",
    tags: ["Recursion", "Dynamic Programming", "Algorithms"],
    progress: 75,
    studentsAffected: 28
  },
  {
    id: 2,
    title: "Midterm Exam Analysis",
    date: "March 12, 2024",
    category: "Assessment",
    priority: "Medium",
    description: "Performance varied significantly. Strongest areas were graph traversal, weakest in greedy algorithms. A significant number of students misinterpreted the 'optimal substructure' concept. Further review sessions on problem decomposition are advised.",
    tags: ["Assessment", "Graph Theory", "Greedy Algorithms"],
    progress: 65,
    studentsAffected: 42
  },
  {
    id: 3,
    title: "Student Engagement Patterns",
    date: "March 15, 2024",
    category: "Behavioral Analysis",
    priority: "Low",
    description: "Analysis of student participation and engagement during lectures and practical sessions. Identified optimal time slots for complex topics and recommended interactive learning approaches. Further review sessions on problem decomposition are advised.",
    tags: ["Engagement", "Learning Patterns", "Behavioral Analysis"],
    progress: 90,
    studentsAffected: 15
  }
];

const Dashboard = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedInsight, setSelectedInsight] = useState(null);

  const handleOpenDialog = (insight) => {
    setSelectedInsight(insight);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedInsight(null);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High': return 'error';
      case 'Medium': return 'warning';
      case 'Low': return 'success';
      default: return 'default';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Algorithm Analysis': return '💻';
      case 'Assessment': return '📊';
      case 'Behavioral Analysis': return '📈';
      default: return 'ℹ️';
    }
  };

  const getProgressColor = (progress) => {
    if (progress >= 80) return 'success';
    if (progress >= 60) return 'warning';
    return 'error';
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Insights Section */}
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={3}>
          {insightsData.map((insight) => (
            <Grid item xs={12} lg={6} xl={4} key={insight.id}>
              <Paper 
                elevation={2} 
                sx={{ 
                  height: '100%', 
                  borderRadius: 3,
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 6
                  }
                }}
              >
                <Box sx={{ 
                  p: 3, 
                  background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                  borderBottom: '1px solid #e0e0e0'
                }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Chip 
                      label={insight.priority}
                      color={getPriorityColor(insight.priority)}
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                    <Chip 
                      label={`${getCategoryIcon(insight.category)} ${insight.category}`}
                      variant="outlined"
                      size="small"
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                    <Typography variant="caption">
                      📅 {insight.date}
                    </Typography>
                  </Box>
                </Box>
                
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ 
                    fontWeight: 600, 
                    mb: 2,
                    color: 'text.primary',
                    lineHeight: 1.3
                  }}>
                    {insight.title}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
                    {insight.description}
                  </Typography>
                  
                  {/* Progress Section */}
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Implementation Progress
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {insight.progress}%
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={insight.progress}
                      color={getProgressColor(insight.progress)}
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Box>
                  
                  {/* Students Affected */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      🎓 {insight.studentsAffected} students affected
                    </Typography>
                  </Box>
                  
                  {/* Tags */}
                  <Box sx={{ mb: 3 }}>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {insight.tags.map((tag, index) => (
                        <Chip 
                          key={index} 
                          label={tag} 
                          variant="outlined" 
                          size="small"
                          sx={{ mb: 0.5 }}
                        />
                      ))}
                    </Stack>
                  </Box>
                  
                  <Button 
                    variant="contained" 
                    color="primary"
                    onClick={() => handleOpenDialog(insight)}
                    fullWidth
                    sx={{ 
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      py: 1.5
                    }}
                  >
                    👁️ View Details
                  </Button>
                </CardContent>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Dialog for Detailed View */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {selectedInsight?.title}
            </Typography>
            <Button onClick={handleCloseDialog} sx={{ minWidth: 'auto' }}>
              ✕
            </Button>
          </Box>
        </DialogTitle>
        
        <Divider />
        
        <DialogContent sx={{ p: 3 }}>
          {selectedInsight && (
            <Box>
              <Box sx={{ mb: 3 }}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                  <Chip 
                    label={`${selectedInsight.priority} Priority`}
                    color={getPriorityColor(selectedInsight.priority)}
                    sx={{ fontWeight: 600 }}
                  />
                  <Chip 
                    label={`${getCategoryIcon(selectedInsight.category)} ${selectedInsight.category}`}
                    variant="outlined"
                  />
                  <Chip 
                    label={`📅 ${selectedInsight.date}`}
                    variant="outlined"
                    size="small"
                  />
                </Stack>
              </Box>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Summary
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
                  {selectedInsight.description}
                </Typography>
                
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Implementation Progress
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {selectedInsight.progress}%
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={selectedInsight.progress}
                    color={getProgressColor(selectedInsight.progress)}
                    sx={{ height: 10, borderRadius: 5 }}
                  />
                </Box>
              </Box>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Tags
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {selectedInsight.tags.map((tag, index) => (
                    <Chip 
                      key={index} 
                      label={tag} 
                      variant="outlined" 
                      size="small"
                    />
                  ))}
                </Stack>
              </Box>
            </Box>
          )}
        </DialogContent>
        
        <Divider />
        
        <DialogActions sx={{ p: 3 }}>
          <Button variant="outlined" onClick={handleCloseDialog} sx={{ borderRadius: 2, textTransform: 'none' }}>
            Close
          </Button>
          {/* <Button 
            variant="contained" 
            color="primary"
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          >
            📥 Export Report
          </Button> */}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Dashboard;
